import { Component, OnInit } from '@angular/core';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { VendorReferral } from '../../../../models/api/view/claim/details/vendor-referrals/vendor-referral';
import { VendorReferralAppointments } from '../../../../models/api/view/claim/details/vendor-referrals/vendor-referral-appointments';
import { BaseComponent } from '../../../base/base.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { TabPanelComponent } from '../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../core/panels/tab-panel/tab.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { VendorReferralAppointmentComponent } from './vendor-referral-appointment.component';
import { VendorReferralDetailComponent } from './vendor-referral-detail.component';

@Component({
  selector: 'app-vendor-referral',
  imports: [
    PageHeaderComponent,
    TableComponent,
    ColumnComponent,
    TabPanelComponent,
    TabComponent,
    VendorReferralAppointmentComponent,
    VendorReferralDetailComponent,
  ],
  template: `<app-page-header label="Vendor Referrals" />
    <app-table
      [dataSource]="vendorReferral"
      [pageSize]="5"
      [scrollable]="false"
      [expandable]="true"
      dataKey="referralID"
      recordLabel="Vendor Referral"
    >
      <app-column
        label="Referral Date"
        name="referralDate"
        width="15%"
        format="d"
      />
      <app-column
        label="Referral Type"
        name="referralDescription"
        width="15%"
        format="d"
      />
      <app-column label="Company Name" name="companyName" width="15%" />
      <app-column label="Issue" name="referralIssue" width="55%">
        <ng-template let-data #value>
          <div
            class="tw-w-full tw-max-w-full tw-min-w-0 tw-whitespace-normal"
            style="word-break: break-word; overflow-wrap: anywhere;"
          >
            {{ data.referralIssue }}
          </div>
        </ng-template>
      </app-column>
      <ng-template #expand let-data>
        <div class="tw-flex tw-gap-8 tw-pb-8">
          <div class="tw-basis-full">
            <app-tab-panel (valueChange)="onTabChange($event, data.referralID)">
              <app-tab label="Details">
                <ng-template #content>
                  <app-vendor-referral-detail [dataSource]="data" />
                </ng-template>
              </app-tab>
              <app-tab label="Appointments">
                <ng-template #content>
                  <app-vendor-referral-appointment
                    [dataSource]="appointmentsCache.get(data.referralID) || []"
                  ></app-vendor-referral-appointment>
                </ng-template>
              </app-tab>
            </app-tab-panel>
          </div>
        </div>
      </ng-template>
    </app-table>`,
  styles: [],
})
export class VendorReferralComponent extends BaseComponent implements OnInit {
  claim: ClaimRow;
  vendorReferral: VendorReferral[] = [];
  vendorReferralAppointments: VendorReferralAppointments[] = [];

  // Cache for appointments by referral ID
  appointmentsCache = new Map<number, VendorReferralAppointments[]>();

  // Track loading state for each referral's appointments
  loadingAppointments = new Set<number>();

  async ngOnInit(): Promise<void> {
    this.claim = this.view.currentClaim();
    this.vendorReferral = await this.api.vendorReferrals.getVendorReferral(
      this.claim.recordSource,
      this.claim.recordId,
    );
    // Transform svcInteractivity values from Y/N to yes/NO
    this.vendorReferral = this.vendorReferral.map((item) => ({
      ...item,
      svcInteractivity:
        item.svcInteractivity === 'Y'
          ? 'yes'
          : item.svcInteractivity === 'N'
            ? 'No'
            : item.svcInteractivity,
    }));
    this.view.hideLoading();
  }

  /**
   * Handle tab change - load appointments only when Appointments tab is selected
   */
  onTabChange(tabValue: string, referralID: number): void {
    // Only load appointments when the Appointments tab is selected
    if (tabValue === 'Appointments') {
      // Check if already cached
      const cached = this.appointmentsCache.get(referralID);
      if (cached) {
        return;
      }

      // If not loading and not cached, trigger loading (only once)
      if (!this.loadingAppointments.has(referralID)) {
        this.loadAppointmentsAsync(referralID);
      }
    }
  }

  /**
   * Async method to load appointments for a referral
   */
  async loadAppointmentsAsync(referralID: number): Promise<void> {
    // Check again to prevent race conditions
    if (
      this.loadingAppointments.has(referralID) ||
      this.appointmentsCache.has(referralID)
    ) {
      return;
    }

    // Mark as loading
    this.loadingAppointments.add(referralID);

    try {
      const appointments =
        await this.api.vendorReferrals.getVendorApppointments(
          this.claim.recordSource,
          this.claim.recordId,
          referralID,
        );

      // Cache the results
      this.appointmentsCache.set(referralID, appointments);
    } finally {
      // Remove from loading set
      this.loadingAppointments.delete(referralID);
    }
  }

  /**
   * Check if appointments are loading for a specific referral
   */
  isAppointmentsLoading(referralID: number): boolean {
    return this.loadingAppointments.has(referralID);
  }
}
